import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { IntegrationPublic } from '@marketing-hub/shared';
import { INTEGRATION_CONNECTOR_LABELS } from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { useGoogleDashboard } from '@/hooks/useGoogleDashboard';
import { SummaryMetricCard } from '@/features/analytics/SummaryMetricCard';
import { EmptyPanel } from '@/features/analytics/EmptyPanel';
import { Ga4TrendChart } from './Ga4TrendChart';
import { GscTrendChart } from './GscTrendChart';
import { sumGa4LastDays } from './ga4Rollup';
import { mergeTopQueriesLastDays, sumGscLastDays } from './gscRollup';
import { formatPercent } from './kpiRollup';
import { DASHBOARD_METRIC_HINTS } from './dashboardMetricHints';
import { IntegrationStatusStrip } from './IntegrationStatusStrip';
import { GoogleCsvExport } from './GoogleCsvExport';
import {
  DashboardChartSkeleton,
  DashboardKpiGridSkeleton,
} from './DashboardSectionSkeleton';
import { ErrorState } from '@/components/StateMessage/StateMessage';
import styles from '../../pages/DashboardPage.module.css';

type Props = {
  rangeDays: 7 | 30 | 90;
  /** Lazy fetch wanneer sectie in beeld (4.2) */
  loadData?: boolean;
};

export function DashboardGoogleSection({ rangeDays, loadData = true }: Props) {
  const { selectedProjectId } = useProjectContext();
  const googleQuery = useGoogleDashboard(rangeDays, { enabled: loadData });

  const integrationsQ = useQuery({
    queryKey: ['integrations', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ integrations: IntegrationPublic[] }>(
        `/api/integrations?project_id=${encodeURIComponent(selectedProjectId!)}`,
      ),
  });

  const integrations = integrationsQ.data?.integrations ?? [];
  const ga4Int = integrations.find((i) => i.connector_type === 'ga4');
  const gscInt = integrations.find((i) => i.connector_type === 'gsc');
  const hasErrorStatus = [ga4Int, gscInt].some((i) => i?.status === 'error');

  const ga4Snapshots = googleQuery.data?.ga4.snapshots ?? [];
  const gscSnapshots = googleQuery.data?.gsc.snapshots ?? [];
  const ga4Roll = sumGa4LastDays(ga4Snapshots, rangeDays);
  const gscRoll = sumGscLastDays(gscSnapshots, rangeDays);
  const gscTop = mergeTopQueriesLastDays(gscSnapshots, rangeDays);
  const hasGoogleData =
    ga4Roll.sessions > 0 || gscRoll.impressions > 0 || gscRoll.clicks > 0;

  const showMetrics = loadData && !googleQuery.isLoading && !googleQuery.isError;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>Google (GA4 &amp; Search Console)</h2>
          <p className={styles.panelHint}>
            Snapshots · zelfde periode als mail ({rangeDays}d). Search Console ~3 dagen achter op
            GA4.
          </p>
        </div>
        <div className={styles.panelActions}>
          <GoogleCsvExport rangeDays={rangeDays} />
          <Link to="/app/integraties">Integraties</Link>
        </div>
      </div>

      {integrationsQ.isLoading && selectedProjectId ? (
        <div className={styles.integrationStripPlaceholder} aria-hidden />
      ) : null}

      {selectedProjectId && integrationsQ.data ? (
        <IntegrationStatusStrip integrations={integrations} />
      ) : !selectedProjectId ? (
        <p className={styles.muted}>
          Selecteer een project voor GA4/GSC per workspace, of bekijk geaggregeerde snapshots
          hieronder.
        </p>
      ) : null}

      {hasErrorStatus ? (
        <div className={styles.syncAlert}>
          <strong>Google-sync aandacht</strong> —{' '}
          <Link to="/app/integraties">Integraties</Link>
          <ul className={styles.syncErrorList}>
            {[ga4Int, gscInt]
              .filter((i) => i?.status === 'error')
              .map((i) =>
                i ? (
                  <li key={i.id}>
                    {INTEGRATION_CONNECTOR_LABELS[i.connector_type]}:{' '}
                    {i.last_error?.slice(0, 120) ?? 'Geen detail — Sync now opnieuw proberen'}
                  </li>
                ) : null,
              )}
          </ul>
        </div>
      ) : null}

      {!loadData ? (
        <>
          <DashboardKpiGridSkeleton count={8} />
          <DashboardChartSkeleton />
        </>
      ) : googleQuery.isLoading ? (
        <>
          <DashboardKpiGridSkeleton count={8} />
          <h3 className={styles.sectionSubtitle}>Trends laden…</h3>
          <DashboardChartSkeleton />
        </>
      ) : googleQuery.isError ? (
        <ErrorState message={(googleQuery.error as Error).message} />
      ) : (
        <>
          {!ga4Int && !gscInt && selectedProjectId && !integrationsQ.isLoading ? (
            <EmptyPanel
              title="Geen GA4/GSC-koppeling"
              description="Koppel Google Analytics en Search Console om sessions, clicks en queries te zien."
              actionLabel="Integraties instellen"
              actionTo="/app/integraties"
            />
          ) : !hasGoogleData && !hasErrorStatus && (ga4Int || gscInt) ? (
            <EmptyPanel
              title="Nog geen snapshot-data"
              description="Sync now haalt tot 30 dagen terug op. GSC-data is vaak pas ~3 dagen na de datum definitief."
              actionLabel="Naar integraties"
              actionTo="/app/integraties"
            />
          ) : null}

          {showMetrics ? (
            <>
              <div className={`${styles.summaryGrid} ${styles.googleMetricGrid}`}>
                <SummaryMetricCard
                  label="Sessions (GA4)"
                  value={String(ga4Roll.sessions)}
                  hideGoalPrompt
                  hint={DASHBOARD_METRIC_HINTS.ga4Sessions}
                />
                <SummaryMetricCard
                  label="Active users"
                  value={String(ga4Roll.active_users)}
                  hideGoalPrompt
                />
                <SummaryMetricCard
                  label="Conversions"
                  value={String(Math.round(ga4Roll.conversions))}
                  hideGoalPrompt
                />
                <SummaryMetricCard
                  label="Engagement (gem.)"
                  value={formatPercent(ga4Roll.engagement_rate)}
                  hideGoalPrompt
                />
                <SummaryMetricCard
                  label="Clicks (GSC)"
                  value={String(gscRoll.clicks)}
                  hideGoalPrompt
                  hint={DASHBOARD_METRIC_HINTS.gscClicks}
                />
                <SummaryMetricCard
                  label="Impressions (GSC)"
                  value={String(gscRoll.impressions)}
                  hideGoalPrompt
                />
                <SummaryMetricCard label="CTR (GSC)" value={formatPercent(gscRoll.ctr)} hideGoalPrompt />
                <SummaryMetricCard
                  label="Positie (gem.)"
                  value={gscRoll.position.toFixed(1)}
                  hideGoalPrompt
                />
              </div>

              <h3 className={styles.sectionSubtitle}>GA4 — trend</h3>
              {ga4Snapshots.length === 0 ? (
                <p className={styles.muted}>Geen GA4-dagen in deze periode.</p>
              ) : (
                <Ga4TrendChart snapshots={ga4Snapshots} />
              )}

              <h3 className={styles.sectionSubtitle}>Search Console — trend</h3>
              {gscSnapshots.length === 0 ? (
                <p className={styles.muted}>Geen GSC-dagen in deze periode.</p>
              ) : (
                <GscTrendChart snapshots={gscSnapshots} />
              )}

              {gscTop.length > 0 ? (
                <>
                  <h3 className={styles.sectionSubtitle}>Top queries ({rangeDays}d)</h3>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Query</th>
                        <th>Clicks</th>
                        <th>Impressions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gscTop.map((q) => (
                        <tr key={q.query}>
                          <td>{q.query}</td>
                          <td>{q.clicks}</td>
                          <td>{q.impressions}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : hasGoogleData ? (
                <p className={styles.muted}>Geen query-rijen in deze periode.</p>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
